import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useState, useEffect } from "react";

const WidgetsModal = ({
  open,
  onClose,
  availableMetrics,
  initialSelectedWidgets,
  onApply,
}) => {
  const [selectedWidgets, setSelectedWidgets] = useState([]);
  const [allWidgetsToggle, setAllWidgetsToggle] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedWidgets([...initialSelectedWidgets]);
      setAllWidgetsToggle(
        initialSelectedWidgets.length === availableMetrics.length
      );
    }
  }, [open, initialSelectedWidgets, availableMetrics.length]);

  const handleToggleWidget = (metricKey) => {
    setSelectedWidgets((prev) => {
      if (prev.includes(metricKey)) {
        const newSelection = prev.filter((key) => key !== metricKey);
        setAllWidgetsToggle(false);
        return newSelection;
      } else {
        const newSelection = [...prev, metricKey];
        setAllWidgetsToggle(newSelection.length === availableMetrics.length);
        return newSelection;
      }
    });
  };

  const handleToggleAllWidgets = (event) => {
    const checked = event.target.checked;
    setAllWidgetsToggle(checked);
    if (checked) {
      setSelectedWidgets(availableMetrics.map((m) => m.key));
    } else {
      setSelectedWidgets([]);
    }
  };

  const handleApply = () => {
    if (selectedWidgets.length < 6) {
      toast.error("Minimum 6 widgets required");
      return;
    }
    onApply(selectedWidgets);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      <DialogTitle
        size="small"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 3,
          gap: 0,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ m: 0 }}>
            Widgets
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ m: 0 }}>
            Pick the metrics you want to track.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "text.secondary",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0,  }}>
        {/* All Widgets Toggle */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 1,
            bgcolor: "rgb(234, 230, 253)",
            borderRadius: 1,
            mb: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
              size="small"
                checked={allWidgetsToggle}
                onChange={handleToggleAllWidgets}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#7558ea",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#7558ea",
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                All Widgets
              </Typography>
            }
          />
          <Typography variant="caption" color="text.secondary">
            NOTE: Minimum 6 widgets required
          </Typography>
        </Box>

        {/* Metrics Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 2.5,
            p: 1,
            pt:0,
            pb:2,
          }}
        >
          {availableMetrics.slice(0, 12).map((metric) => {
            const isSelected = selectedWidgets.includes(metric.key);
            return (
              <Box
                key={metric.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1,
                  py: 0.5,
                }}
              >
                <Switch
                  checked={isSelected}
                  size="small"
                  onChange={() => handleToggleWidget(metric.key)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#7558ea",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#7558ea",
                    },
                  }}
                />
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                  {metric?.shortTitle?.toUpperCase()}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: "1px solid #E0E0E0",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            borderColor: "#7558ea",
            color: "#7558ea",
            "&:hover": {
              borderColor: "#7558ea",
              bgcolor: "rgba(117, 88, 234, 0.04)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          size="small"
          sx={{
            textTransform: "none",
            bgcolor: "#7558ea",
            "&:hover": {
              bgcolor: "#6548d9",
            },
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WidgetsModal;

